/**
 * AccordionGallery — a horizontal, accordion-style row of panels the person can flick through.
 * One panel is "active" at a time: it opens to the frame's 360px while the rest hold a 90px spine,
 * and moving between them animates the widths and a light 3D tilt.
 *
 * SIZING: the widths are ABSOLUTE, taken from the Figma uploads frame (active 360x270, spine 90x270 —
 * the scaled-up form of the 240x180 the design was laid out at). They are declared once in
 * accordionGallery.css as `--ag-w-active` / `--ag-w-spine` and animated on `flex-basis`. An earlier
 * version animated `flex-grow` against a full-width row, which can only express a RATIO of the
 * container: the active panel came out between 328px and 1017px depending on the tool count and the
 * viewport, so it matched the frame at no width at all. If you need different proportions, override
 * the two custom properties at the call site rather than reintroducing grow.
 *
 * ORIGIN: adapted from reactbits.dev's AccordionGallery. The markup, CSS, props and accessibility
 * are kept faithful to the original; the ONLY substantive change is the animation engine. The
 * upstream component drives an imperative GSAP timeline — we drive the same transitions with
 * `motion` (Framer Motion), which is already a dependency of this app and is the animation library
 * the whole design-system reskin is built on. Adding GSAP would ship a second animation library and
 * a parallel reduced-motion mechanism; motion gives us the same result through the app's existing
 * `<MotionConfig reducedMotion="user">` and `useReducedMotion()`.
 *
 * REDUCED MOTION: this is layer 3 of the app's three-layer policy. `useReducedMotion()` reads the
 * same `prefers-reduced-motion` signal `<MotionConfig>` gates on, so when the person asks for
 * reduced motion we snap to the target layout with `duration: 0` instead of animating. (The CSS also
 * carries a `@media (prefers-reduced-motion: reduce)` fallback for the non-JS transform hints.)
 *
 * ACCESSIBILITY: the row is `role="list"`; each panel is a `role="listitem"`, focusable, with
 * `aria-current` on the active one and an `aria-label`. Arrow keys walk the panels (Left/Right,
 * Home/End); Enter/Space activate. Focus shows a design-token accent ring (`:focus-visible`).
 */
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useAnimate, useReducedMotion } from 'motion/react';
import './accordionGallery.css';

export interface AccordionGalleryItem {
  /** Stable identity for the panel (used as the React key and passed back on activate). */
  id: string;
  /** Image URL for the panel media. Omit to render the design-token placeholder fill. */
  image?: string | null;
  /** Visible label shown on the active panel and used as the panel's accessible name. */
  label: string;
  /** Optional secondary line under the label on the active panel (e.g. status). */
  sublabel?: string | null;
  /** Optional href — when set the panel renders as an <a>; otherwise a <div>. */
  href?: string | null;
  /**
   * Panel treatment. 'tool' (the default) is a normal item panel. 'summary' marks a TERMINAL panel
   * that stands for the whole collection rather than one tool — the "view all" tile at the end of the
   * row. It takes its own fill so it reads as a different kind of thing, not another tool, and it
   * never shows a media placeholder (there is no single image for "all of them").
   *
   * A 'summary' panel is also FIXED-WIDTH: it never expands. Expanding is how a tool panel says "you
   * are looking at me"; this panel is a permanent destination, so it holds one narrow spine width and
   * shows its icon at all times. See `isExpandable` below.
   */
  variant?: 'tool' | 'summary';
  /**
   * Optional glyph pinned to the panel's TOP-RIGHT — a state marker (e.g. a padlock for a withdrawn
   * submission), not a control. It must never be the only carrier of its meaning: pass the same fact
   * in `sublabel` text too, and render the node `aria-hidden`.
   */
  badge?: ReactNode;
  /**
   * Replaces the media area with a node of the caller's own, filling the panel (`.ag-panel__icon` is
   * `inset: 0`). It wins over BOTH `image` and the placeholder fill, so setting it means the panel's
   * mint `--ag-placeholder` tint never renders.
   *
   * Two callers, for two different reasons. The terminal summary tile uses it for a centred GLYPH,
   * because it has no thumbnail and no single image that could represent "all of them". Profile.tsx's
   * submission panels use it for each tool's GENERATED preview art, because ProfileToolSummary carries
   * a name but no image URL — `image` is a URL and that is a node, which is the whole distinction
   * between the two fields.
   *
   * A node here must be `aria-hidden`: `label` is already the panel's accessible name.
   */
  icon?: ReactNode;
}

export interface AccordionGalleryProps {
  items: AccordionGalleryItem[];
  /** Index of the panel that starts active. Clamped into range. */
  initialActive?: number;
  /** Desaturate inactive panels. Own-uploads read better in colour, so this defaults off. */
  grayscale?: boolean;
  /** Fired when a panel is activated by click/Enter/Space. */
  onActivate?: (item: AccordionGalleryItem, index: number) => void;
  /** Extra class on the root, for layout overrides at the call site. */
  className?: string;
  /**
   * Lay the ACTIVE panel's label out like a bento tile — media block on top, text bottom-LEFT with
   * the title above the sublabel — instead of the Figma frame's centred label. Off by default so
   * every other caller keeps the drawn behaviour; "Your Uploads" turns it on so an expanded upload
   * panel and a Tool Collection bento tile read as the same family of card.
   */
  bentoLabel?: boolean;
}

// Timing kept close to the upstream GSAP feel: a brisk, eased width change.
const DURATION = 0.55;
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]; // easeOutExpo-ish

/**
 * Can this panel take the expanded slot? Everything except a terminal 'summary' tile, which is a
 * fixed-width destination. Centralised so the layout pass, the initial-index clamp, the pointer
 * handlers and the keyboard walk all agree — if any one of them disagreed, hovering the summary tile
 * would collapse every tool panel to a spine and leave nothing expanded.
 */
function isExpandable(item: AccordionGalleryItem): boolean {
  return item.variant !== 'summary';
}

export default function AccordionGallery({
  items,
  initialActive = 0,
  grayscale = false,
  onActivate,
  className,
  bentoLabel = false,
}: AccordionGalleryProps) {
  // The initial index must land on an EXPANDABLE panel; if it points at the fixed summary tile (or the
  // list is all summary), fall back to the first expandable one, else 0.
  const rawInitial = items.length ? Math.min(Math.max(initialActive, 0), items.length - 1) : 0;
  const firstExpandable = items.findIndex(isExpandable);
  const clampedInitial =
    items[rawInitial] && isExpandable(items[rawInitial])
      ? rawInitial
      : firstExpandable >= 0
        ? firstExpandable
        : 0;
  const [activeRaw, setActive] = useState(clampedInitial);
  const [scope, animate] = useAnimate();
  const reduceMotion = useReducedMotion();

  /**
   * The panel widths, read from the row's own `--ag-w-active` / `--ag-w-spine` rather than duplicated
   * as constants here. The stylesheet is where the Figma measurements live, so a call site that
   * overrides the pair gets the animation for free; hardcoding them in JS would let the two drift.
   * The fallbacks match the CSS defaults so a missing stylesheet still animates to sane widths.
   */
  const readWidths = useCallback((): { activeWidth: string; spineWidth: string } => {
    const row = scope.current;
    if (!row) return { activeWidth: '360px', spineWidth: '90px' };
    const cs = getComputedStyle(row);
    const active = cs.getPropertyValue('--ag-w-active').trim();
    const spine = cs.getPropertyValue('--ag-w-spine').trim();
    return {
      activeWidth: active || '360px',
      spineWidth: spine || '90px',
    };
  }, [scope]);

  // Clamp at read time rather than syncing via an effect: if the item list shrinks under the
  // stored index, this keeps `active` in range without an extra render (derived state, not an
  // effect — that's what the react-hooks/set-state-in-effect rule steers toward).
  const active = items.length ? Math.min(activeRaw, items.length - 1) : 0;

  // Per-panel refs so we can animate flexGrow / the media parallax without React re-rendering.
  const panelRefs = useRef<Array<HTMLElement | null>>([]);
  const mediaRefs = useRef<Array<HTMLElement | null>>([]);

  const applyLayout = useCallback(
    (animated: boolean) => {
      const duration = animated && !reduceMotion ? DURATION : 0;
      const { activeWidth, spineWidth } = readWidths();
      panelRefs.current.forEach((panel, i) => {
        if (!panel) return;
        const item = items[i];
        // The terminal summary tile is held OUT of the accordion entirely — the CSS pins its basis at
        // 72px and nothing here animates it, so its width is identical in every state.
        if (item && !isExpandable(item)) return;
        const isActive = i === active;
        // WIDTH IS ANIMATED ON flex-basis, NOT flex-grow. The panels are the Figma frame's measured
        // 360 (active) and 90 (spine), so the row needs absolute widths; flex-grow can only express a
        // RATIO of whatever space the container has, which is why the active panel previously rendered
        // anywhere from 328px to 1017px depending on the tool count and the viewport. Reading the two
        // widths from the CSS custom properties keeps the numbers declared in one place (the
        // stylesheet) and lets a call site override the pair without touching this file.
        animate(
          panel,
          { flexBasis: isActive ? activeWidth : spineWidth },
          { duration, ease: EASE },
        );

        const media = mediaRefs.current[i];
        if (media) {
          // A light parallax: inactive media pulls slightly toward the active panel and dims.
          const dir = i < active ? 1 : i > active ? -1 : 0;
          animate(
            media,
            {
              x: isActive ? '0%' : `${dir * 8}%`,
              scale: isActive ? 1 : 1.08,
              opacity: isActive ? 1 : 0.55,
            },
            { duration, ease: EASE },
          );
        }
      });
    },
    [active, animate, items, reduceMotion, readWidths],
  );

  // First paint: snap to the initial layout (no animation), then animate on every change after.
  const mounted = useRef(false);
  useLayoutEffect(() => {
    applyLayout(mounted.current);
    mounted.current = true;
  }, [applyLayout]);

  function activate(index: number) {
    const item = items[index];
    if (!item) return;
    // A fixed panel never takes the expanded slot, so leave `active` where it is — but still report
    // the activation, because the caller decides what a click there means (the summary tile navigates
    // via its own href; SubmissionsSection just declines to open the per-tool overlay for it).
    if (index !== active && isExpandable(item)) setActive(index);
    onActivate?.(item, index);
  }

  /**
   * Move the expanded slot toward `from + step`, SKIPPING fixed panels. Returns the index actually
   * landed on, or null when there is no expandable panel that way (so the caller leaves focus put).
   */
  function seekExpandable(from: number, step: number): number | null {
    for (let i = from + step; i >= 0 && i < items.length; i += step) {
      const item = items[i];
      if (item && isExpandable(item)) return i;
    }
    return null;
  }

  function handleKeyDown(e: React.KeyboardEvent, index: number) {
    switch (e.key) {
      // Arrow keys move the EXPANDED slot, so they walk between expandable panels and step over the
      // fixed summary tile. That tile is still reachable — it is in the tab order and Enter activates
      // it — it just is not a stop on the accordion's own left/right walk, because there is nothing to
      // expand there.
      case 'ArrowRight':
      case 'ArrowDown': {
        e.preventDefault();
        const next = seekExpandable(index, 1);
        if (next === null) break;
        setActive(next);
        panelRefs.current[next]?.focus();
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        e.preventDefault();
        const prev = seekExpandable(index, -1);
        if (prev === null) break;
        setActive(prev);
        panelRefs.current[prev]?.focus();
        break;
      }
      case 'Home': {
        e.preventDefault();
        const first = items.findIndex(isExpandable);
        if (first < 0) break;
        setActive(first);
        panelRefs.current[first]?.focus();
        break;
      }
      case 'End': {
        e.preventDefault();
        // The LAST expandable panel, not items.length-1 — that is the fixed summary tile.
        const last = seekExpandable(items.length, -1);
        if (last === null) break;
        setActive(last);
        panelRefs.current[last]?.focus();
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        activate(index);
        break;
      }
      default:
        break;
    }
  }

  if (!items.length) return null;

  return (
    <div
      ref={scope}
      role="list"
      className={`accordion-gallery${grayscale ? ' accordion-gallery--grayscale' : ''}${
        bentoLabel ? ' accordion-gallery--bento' : ''
      }${className ? ` ${className}` : ''}`}
    >
      {items.map((item, i) => {
        const isActive = i === active;
        const commonProps = {
          role: 'listitem',
          className: `ag-panel${isActive ? ' ag-panel--active' : ''}${
            item.variant === 'summary' ? ' ag-panel--summary' : ''
          }`,
          tabIndex: 0,
          'aria-current': isActive && isExpandable(item) ? ('true' as const) : undefined,
          'aria-label': item.sublabel ? `${item.label} — ${item.sublabel}` : item.label,
          ref: (el: HTMLElement | null) => {
            panelRefs.current[i] = el;
          },
          // Hovering or focusing a FIXED panel must not steal the expanded slot — otherwise every tool
          // panel would collapse to a spine with nothing expanded in the row.
          onMouseEnter: () => {
            if (isExpandable(item)) setActive(i);
          },
          onFocus: () => {
            if (isExpandable(item)) setActive(i);
          },
          onClick: (e: React.MouseEvent) => {
            // Let real navigation happen for <a>; for <div> panels the click IS the activation.
            if (!item.href) e.preventDefault();
            activate(i);
          },
          onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, i),
        };

        const inner = (
          <div className="ag-panel__frame">
            <div
              className="ag-panel__media"
              ref={(el) => {
                mediaRefs.current[i] = el;
              }}
            >
              {item.icon ? (
                // A glyph standing for the whole panel (the summary tile). Centred, and never paired
                // with a placeholder fill — the icon IS the panel's visual.
                <div className="ag-panel__icon" aria-hidden="true">
                  {item.icon}
                </div>
              ) : item.image ? (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt -- alt is the tool name, intentional
                <img src={item.image} alt={item.label} draggable={false} />
              ) : item.variant === 'summary' ? null : (
                <div className="ag-panel__placeholder" aria-hidden="true" />
              )}
            </div>
            <div className="ag-panel__overlay" aria-hidden="true" />
            {/* State marker pinned top-right (e.g. the withdrawn padlock). Presentational only — the
                same fact is in the sublabel text, so the glyph itself is aria-hidden by its author. */}
            {item.badge && <div className="ag-panel__badge">{item.badge}</div>}
            <div className="ag-panel__label">
              <span className="ag-panel__title">{item.label}</span>
              {item.sublabel && <span className="ag-panel__sub">{item.sublabel}</span>}
            </div>
          </div>
        );

        return item.href ? (
          <a key={item.id} href={item.href} {...commonProps}>
            {inner}
          </a>
        ) : (
          <div key={item.id} {...commonProps}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}
